/**
 * question-runtime — how a set of booking questions BEHAVES when someone fills it
 * in: which items are visible (show-if + jump rules), and one field per type that
 * honours every per-type setting. Shared by the group editor's Preview tab and
 * Event Day's Add guest form, so both run the same rules (2026-10-07,
 * field-types audit). Answer values follow the contract's question-answers.ts:
 *   choice → string[] of option ids · yesno → true | false | null ·
 *   stepper → { rowId: count } · number → number | null · date → "YYYY-MM-DD" ·
 *   text/datetime → string · "Other" text rides beside the value as `<qid>::other`.
 * Data: unbound — renders and evaluates; submitting is the caller's op.
 */
import { type CSSProperties, type ReactNode } from "react";
import { T, Toggle, QuantityStepper } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import { type Question, type GroupItem, type QBranchWhen } from "./useQuestions";
import { displayOptions, emptySelectConfig, stepperRows, clampStep, fileAccept, emptyFileConfig } from "./question-model";
import { infoToneStyle } from "./QuestionConfigCards";

export const otherKey = (qid: string) => `${qid}::other`;

/** What an untouched field holds: a prefilled text, or a toggle's starting state. */
export function startValue(q: Question): unknown {
  if (q.type === "yesno") return q.yesno?.startsAs === "yes" ? true : q.yesno?.startsAs === "no" ? false : undefined;
  if ((q.type === "short" || q.type === "long") && q.defaultValue) return q.defaultValue;
  return undefined;
}
/** answers with every untouched question's starting value filled in — what is shown, evaluated and submitted. */
export function withStartValues(qById: Record<string, Question>, answers: Record<string, unknown>): Record<string, unknown> {
  const out = { ...answers };
  for (const id of Object.keys(qById)) if (out[id] === undefined) { const v = startValue(qById[id]); if (v !== undefined) out[id] = v; }
  return out;
}

export function conditionHolds(when: QBranchWhen, value: unknown, q?: Question): boolean {
  if (when.kind === "option") return Array.isArray(value) ? value.includes(when.optionId) : value === when.optionId;
  if (when.kind === "toggle") return when.state === "unanswered" ? value == null : when.state === "on" ? value === true : value === false;
  const row = stepperRows(q?.stepper).find((r) => r.id === when.rowId);
  const raw = value && typeof value === "object" ? (value as Record<string, number>)[when.rowId] : undefined;
  const n = typeof raw === "number" ? raw : row ? clampStep(row, row.defaultValue ?? 0) : 0;
  switch (when.op) { case "eq": return n === when.count; case "gte": return n >= when.count; case "lte": return n <= when.count; case "gt": return n > when.count; default: return n < when.count; }
}

/** Jump target for an answered item: typed branches first, then legacy option-keyed rules (first picked option in option order). */
function jumpTarget(it: GroupItem, q: Question, value: unknown): string | null {
  for (const b of it.branches || []) if (conditionHolds(b.when, value, q)) return b.goTo;
  const rules = it.rules || {};
  if (!Object.keys(rules).length) return null;
  if (q.type === "yesno") return value === true ? rules.yes ?? null : value === false ? rules.no ?? null : null;
  const picked = Array.isArray(value) ? value : [];
  for (const o of q.options || []) if (picked.includes(o.id) && rules[o.id]) return rules[o.id];
  return null;
}

/** The items a respondent currently sees, in order. Show-if conditions are each checked on their own (any one shows the item). */
export function visibleItems(items: GroupItem[], qById: Record<string, Question>, answers: Record<string, unknown>): GroupItem[] {
  const out: GroupItem[] = [];
  const shown = new Set<string>();
  let skipTo: string | null = null;
  for (const it of items) {
    const q = qById[it.qid]; if (!q) continue;
    if (skipTo) { if (it.qid !== skipTo) continue; skipTo = null; }
    if (it.showIf?.length && !it.showIf.some((c) => shown.has(c.qid) && conditionHolds(c.when, answers[c.qid], qById[c.qid]))) continue;
    out.push(it); shown.add(it.qid);
    const target = jumpTarget(it, q, answers[it.qid]);
    if (target === "__end__") break;
    if (target) skipTo = target;
  }
  return out;
}

/** First problem with an answer, or null — the same checks the server runs, phrased for a person. */
export function answerIssue(q: Question, required: boolean, value: unknown, other?: string): string | null {
  const empty = value == null || value === "" || (Array.isArray(value) && value.length === 0);
  if (required && empty && q.type !== "stepper") return "required";
  if (empty) return null;
  if (Array.isArray(value)) {
    const sel = q.select; const n = value.length;
    if (sel?.minSelections != null && n < sel.minSelections) return `min:${sel.minSelections}`;
    if (sel?.maxSelections != null && n > sel.maxSelections) return `max:${sel.maxSelections}`;
    const otherOpt = (q.options || []).find((o) => o.allowsText);
    if (otherOpt && value.includes(otherOpt.id) && !(other || "").trim()) return "other";
  }
  if (q.type === "number" && typeof value === "number" && q.number) {
    if (q.number.min != null && value < q.number.min) return `nmin:${q.number.min}`;
    if (q.number.max != null && value > q.number.max) return `nmax:${q.number.max}`;
  }
  if (q.type === "date" && typeof value === "string" && q.date) {
    if (q.date.earliest && value < q.date.earliest) return `dmin:${q.date.earliest}`;
    if (q.date.latest && value > q.date.latest) return `dmax:${q.date.latest}`;
  }
  return null;
}

const fieldBase = (extra?: CSSProperties): CSSProperties => ({ width: "100%", height: 42, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 14, fontFamily: T.font, boxSizing: "border-box", color: T.n1, background: T.white, ...extra });

export function RuntimeField({ q, required, value, other, onChange, onOther, today }: {
  q: Question; required: boolean; value: unknown; other?: string;
  onChange: (v: unknown) => void; onOther?: (text: string) => void; today?: string;
}) {
  const t = useT();
  const ro = !!q.textRules?.readOnly || (q.type === "yesno" && !!q.yesno?.readOnly);
  const issue = answerIssue(q, required, value, other);
  const issueText = (() => {
    if (!issue || issue === "required") return null; // required shows on submit, not while typing
    const [k, v] = issue.split(":");
    if (k === "min") return t("questions.runtime.minPicks", "Choose at least {n}.", { n: v });
    if (k === "max") return t("questions.runtime.maxPicks", "Choose at most {n}.", { n: v });
    if (k === "other") return t("questions.runtime.otherNeeded", "Tell us what “Other” means.");
    if (k === "nmin") return t("questions.runtime.numMin", "Enter {n} or more.", { n: v });
    if (k === "nmax") return t("questions.runtime.numMax", "Enter {n} or less.", { n: v });
    if (k === "dmin") return t("questions.runtime.dateMin", "Choose {d} or later.", { d: v });
    return t("questions.runtime.dateMax", "Choose {d} or earlier.", { d: v });
  })();
  const label: ReactNode = (
    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, marginBottom: 6 }}>
      {q.label}{required ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}
      {q.help ? <span style={{ display: "block", fontSize: 12, fontWeight: 400, color: T.soft, marginTop: 2 }}>{q.help}</span> : null}
    </div>
  );
  const wrap = (control: ReactNode) => <div>{label}{control}{issueText ? <div role="alert" style={{ fontSize: 12, color: T.red1, marginTop: 5 }}>{issueText}</div> : null}</div>;

  if (q.type === "heading") return <div style={{ fontSize: 15.5, fontWeight: 700, color: T.n1, borderBottom: `1px solid ${T.border}`, paddingBottom: 8 }}>{q.label}</div>;
  if (q.type === "label") {
    const tone = infoToneStyle(q.info?.tone ?? "plain");
    if (!tone) return <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.55 }}>{q.label}</div>;
    return (
      <div style={{ display: "flex", gap: 10, padding: 12, background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 10 }}>
        <span style={{ color: tone.fg, display: "inline-flex", marginTop: 1 }}>{tone.icon}</span>
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {q.info?.title ? <b style={{ fontSize: 13, color: T.n1 }}>{q.info.title}</b> : null}
          <span style={{ fontSize: 13, color: T.n1, lineHeight: 1.5 }}>{q.label}</span>
        </span>
      </div>
    );
  }
  const str = typeof value === "string" ? value : "";
  switch (q.type) {
    case "short": case "datetime":
      return wrap(<input type={q.type === "datetime" ? "datetime-local" : "text"} value={str} readOnly={ro} placeholder={q.placeholder || ""} maxLength={q.textRules?.validationEnabled && q.textRules.maxLength ? q.textRules.maxLength : undefined} onChange={(e) => onChange(e.target.value)} style={fieldBase(ro ? { background: T.n6, color: T.n3 } : undefined)} />);
    case "long":
      return wrap(<textarea value={str} readOnly={ro} rows={3} placeholder={q.placeholder || ""} maxLength={q.textRules?.validationEnabled && q.textRules.maxLength ? q.textRules.maxLength : undefined} onChange={(e) => onChange(e.target.value)} style={fieldBase({ height: "auto", padding: 10, resize: "vertical", ...(ro ? { background: T.n6, color: T.n3 } : {}) })} />);
    case "number": {
      const d = q.number?.decimals ?? 0;
      return wrap(
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input inputMode={d ? "decimal" : "numeric"} readOnly={ro} value={typeof value === "number" ? String(value) : ""} placeholder={q.placeholder || "0"}
            onChange={(e) => { const raw = e.target.value.replace(d ? /[^0-9.\-]/g : /[^0-9\-]/g, ""); const n = d ? parseFloat(raw) : parseInt(raw, 10); onChange(raw === "" || Number.isNaN(n) ? null : n); }}
            style={fieldBase({ width: 160, ...(ro ? { background: T.n6, color: T.n3 } : {}) })} />
          {q.number?.unit ? <span style={{ fontSize: 13, color: T.soft }}>{q.number.unit}</span> : null}
        </span>,
      );
    }
    case "date":
      return wrap(<input type="date" value={str} min={q.date?.notInPast && today && (!q.date?.earliest || today > q.date.earliest) ? today : q.date?.earliest ?? undefined} max={q.date?.latest ?? undefined} onChange={(e) => onChange(e.target.value || null)} style={fieldBase({ width: 200 })} />);
    case "yesno": {
      const on = value === true;
      return wrap(
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, opacity: value == null ? 0.75 : 1 }}>
          <Toggle on={on} disabled={ro} onChange={(next) => onChange(next)} label={q.label} />
          <span style={{ fontSize: 13.5, color: value == null ? T.soft : T.n1 }}>{value == null ? t("questions.yesno.unanswered", "Not answered yet") : on ? t("common.yes", "Yes") : t("common.no", "No")}</span>
        </span>,
      );
    }
    case "radio": case "select": case "checks": case "multi": {
      const cfg = q.select ?? emptySelectConfig();
      const multi = q.type === "checks" || q.type === "multi" || (q.type === "select" && cfg.allowMultiple);
      const picked = Array.isArray(value) ? (value as string[]) : [];
      const opts = displayOptions(q.options || [], cfg);
      const otherOpt = (q.options || []).find((o) => o.allowsText);
      const toggle = (id: string) => onChange(multi ? (picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]) : [id]);
      return wrap(
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {opts.map((o) => {
            const sel = picked.includes(o.id);
            return (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: T.n1, cursor: "pointer", padding: "8px 11px", border: `1.5px solid ${sel ? T.p1 : T.n4}`, borderRadius: 8, background: sel ? T.p4 : T.white }}>
                <input type={multi ? "checkbox" : "radio"} checked={sel} onChange={() => toggle(o.id)} style={{ margin: 0 }} />
                {o.label}
              </label>
            );
          })}
          {otherOpt && picked.includes(otherOpt.id) ? (
            <input value={other ?? ""} onChange={(e) => onOther?.(e.target.value)} placeholder={t("questions.runtime.otherPlaceholder", "Please specify")} maxLength={500} style={fieldBase()} />
          ) : null}
        </div>,
      );
    }
    case "stepper": {
      const vals = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, number>) : {};
      return wrap(
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {stepperRows(q.stepper).map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ flex: 1, fontSize: 13.5, color: T.n1 }}>{r.label}{r.helper ? <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{r.helper}</span> : null}</span>
              <QuantityStepper value={vals[r.id] ?? clampStep(r, r.defaultValue ?? 0)} min={r.min ?? undefined} max={r.max ?? undefined} size="sm" ariaLabel={r.label || q.label}
                onChange={(n) => onChange({ ...vals, [r.id]: clampStep(r, n) })} />
            </div>
          ))}
        </div>,
      );
    }
    case "file": {
      const f = q.file ?? emptyFileConfig();
      return wrap(<input type="file" accept={fileAccept(f) || undefined} multiple={f.allowMultiple} onChange={(e) => onChange([...(e.target.files ?? [])].map((x) => x.name))} style={fieldBase({ height: "auto", padding: 10, border: `1px dashed ${T.n4}` })} />);
    }
    default:
      return wrap(<input value={str} onChange={(e) => onChange(e.target.value)} style={fieldBase()} />);
  }
}

/** A whole group as a respondent sees it — only visible items, with live show-if and jumps. */
export function RuntimeGroupForm({ items, qById, answers, setA, today }: {
  items: GroupItem[]; qById: Record<string, Question>; answers: Record<string, unknown>;
  setA: (key: string, v: unknown) => void; today?: string;
}) {
  const eff = withStartValues(qById, answers);
  const shown = visibleItems(items, qById, eff);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {shown.map((it) => {
        const q = qById[it.qid];
        const req = q.hiddenInBooking ? false : it.required != null ? it.required : q.required;
        return <RuntimeField key={it.qid} q={q} required={!!req} value={eff[it.qid]} other={eff[otherKey(it.qid)] as string | undefined}
          onChange={(v) => setA(it.qid, v)} onOther={(txt) => setA(otherKey(it.qid), txt)} today={today} />;
      })}
    </div>
  );
}
